import * as React from "react";

type XAnchor = "center" | "left" | "right";
type YAnchor = "bottom" | "center" | "top";
type XYAnchor = XAnchor | YAnchor | `${Exclude<YAnchor, "center">}-${Exclude<XAnchor, "center">}`;
export type PopoverXYAnchor = XYAnchor;

type PopoverProps = {
    children        : React.ReactNode,
    open            : boolean,
    position        : {
        bottom ?: number,
        left   ?: number,
        right  ?: number,
        top    ?: number,
    },
};

export const Popover = React.forwardRef((
    {children, open, position}:PopoverProps,
    ref?: React.Ref<HTMLDivElement>,
) => !open ? null : (
    <div
        ref={ref}
        style={{...position, zIndex: 1, position: "absolute"}}
    >{children}</div>
));

export const usePopoverAutoClose = (
    setOpen: (open: boolean) => void,
    popoverDiv: HTMLDivElement|null,
    elementsToIgnore: HTMLElement[] = [],
) =>
{
    React.useEffect(() =>
    {
        if (popoverDiv) {
            const handleAnyClick = (e:MouseEvent) =>
            {
                if (
                    e.target !== popoverDiv
                    && !popoverDiv!.contains(e.target as any)
                    && !elementsToIgnore.some(trigger => trigger === e.target || trigger?.contains(e.target as any))
                ) {
                    setOpen(false);
                }
            };
            document.body.addEventListener("click", handleAnyClick);
            return () => document.body.removeEventListener("click", handleAnyClick);
        }
        return () => {};
    }, [popoverDiv]);
};

export type PopoverTargetPosition = (
    | {anchor: XYAnchor, rect: DOMRect, offset?: {x?: number, y?: number}}
    | {x: number, y: number}
);
export const usePopoverPosition = ({containerOffset={left: 0, top: 0}, popover, target}:{
    containerOffset ?: {left: number, top: number},
    popover          : {anchor: XYAnchor, rect: DOMRect},
    target           : PopoverTargetPosition
}) =>
{
    const targetIsRect = "rect" in target;
    if (!popover.rect || (targetIsRect && !target.rect)) return {left: 0, top: 0};

    const anchorOffset = getAnchorOffset(popover.rect, popover.anchor);
    const anchorPoint  = (
        targetIsRect
        ? movePoint(getAnchorPoint(target.rect, target.anchor), target.offset ?? {})
        : target
    );

    const left = Math.max(
        0 - containerOffset.left,
        Math.min(
            document.body.clientWidth - popover.rect.width,
            anchorPoint.x - containerOffset.left + anchorOffset.left
        )
    );
    const top = Math.max(
        0 - containerOffset.top,
        Math.min(
            document.body.clientHeight - popover.rect.height,
            anchorPoint.y - containerOffset.top + anchorOffset.top
        )
    );

    return {left, top};
};

const getAnchorComponents = (anchor: XYAnchor) =>
{
    const xAnchor:XAnchor = (/left|right/.exec(anchor)?.[0] as XAnchor) ?? "center";
    const yAnchor:YAnchor = (/top|bottom/.exec(anchor)?.[0] as YAnchor) ?? "center";
    return {xAnchor, yAnchor};
};

const getAnchorOffset = (rect: DOMRect, anchor: XYAnchor) =>
{
    const {xAnchor, yAnchor} = getAnchorComponents(anchor);
    const left = (
        xAnchor === "left"
        ? 0
        : -rect.width / (xAnchor === "right" ? 1 : 2)
    );
    const top = (
        yAnchor === "top"
        ? 0
        : -rect.height / (yAnchor === "bottom" ? 1 : 2)
    );
    return {left, top};
};

const getAnchorPoint = (rect: DOMRect, anchor: XYAnchor) =>
{
    const {xAnchor, yAnchor} = getAnchorComponents(anchor);
    return {
        x: (
            xAnchor === "left"
            ? rect.left
            : (
                xAnchor === "right"
                ? rect.right
                : rect.left + (rect.width / 2)
            )
        ),
        y: (
            yAnchor === "top"
            ? rect.top
            : (
                yAnchor === "bottom"
                ? rect.bottom
                : rect.top + (rect.height / 2)
            )
        ),
    };
};

const movePoint = (point: {x: number, y: number}, offset: {x?: number, y?: number}) => ({
    x: point.x + (offset.x ?? 0),
    y: point.y + (offset.y ?? 0),
});

